import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "sonner";
import ProtectedRoute from "@/components/ProtectedRoute";
import AppShell from "@/components/AppShell";

import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import SalesCRMPage from "@/pages/SalesCRMPage";
import MembersPage from "@/pages/MembersPage";
import AttendancePage from "@/pages/AttendancePage";
import CoachPage from "@/pages/CoachPage";
import RetentionPage from "@/pages/RetentionPage";
import ReferralsPage from "@/pages/ReferralsPage";
import ReportsPage from "@/pages/ReportsPage";
import FinancePage from "@/pages/FinancePage";
import SchedulePage from "@/pages/SchedulePage";
import CustomerHubPage from "@/pages/CustomerHubPage";
import OperationsPage from "@/pages/OperationsPage";
import ApprovalsPage from "@/pages/ApprovalsPage";
import MarketingPage from "@/pages/MarketingPage";
import RewardSettingsPage from "@/pages/RewardSettingsPage";
import RedemptionsAdminPage from "@/pages/RedemptionsAdminPage";
import JobsPage from "@/pages/JobsPage";
import PublicReferralPage from "@/pages/PublicReferralPage";
import AssessmentsPage from "@/pages/AssessmentsPage";
import StaffPage from "@/pages/StaffPage";
import ExpensesPage from "@/pages/ExpensesPage";
import LeadFormEmbed from "@/pages/LeadFormEmbed";
import WebsiteEmbedPage from "@/pages/WebsiteEmbedPage";
import BulkImportPage from "@/pages/BulkImportPage";

function RoleRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "CUSTOMER") return <Navigate to="/me" replace />;
  return <Navigate to="/dashboard" replace />;
}

function Shelled({ children }) {
  return <AppShell>{children}</AppShell>;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster theme="dark" position="top-right" toastOptions={{
          style: { background: "#0a0a0a", border: "1px solid #27272a", color: "#fafafa", fontFamily: "Manrope, sans-serif" },
        }} />
        <Routes>
          <Route path="/login" element={<LoginPage />} />

          {/* Public referral link — no auth */}
          <Route path="/r/:referrerId" element={<PublicReferralPage />} />
          <Route path="/embed/lead" element={<LeadFormEmbed />} />

          <Route path="/" element={<RoleRedirect />} />

          <Route path="/dashboard" element={
            <ProtectedRoute roles={["SUPER_ADMIN", "ADMIN", "COACH"]}><Shelled><DashboardPage /></Shelled></ProtectedRoute>
          } />
          <Route path="/sales" element={
            <ProtectedRoute roles={["SUPER_ADMIN", "ADMIN"]}><Shelled><SalesCRMPage /></Shelled></ProtectedRoute>
          } />
          <Route path="/members" element={
            <ProtectedRoute roles={["SUPER_ADMIN", "ADMIN", "COACH"]}><Shelled><MembersPage /></Shelled></ProtectedRoute>
          } />
          <Route path="/attendance" element={
            <ProtectedRoute roles={["SUPER_ADMIN", "ADMIN", "COACH"]}><Shelled><AttendancePage /></Shelled></ProtectedRoute>
          } />
          <Route path="/coach" element={
            <ProtectedRoute roles={["SUPER_ADMIN", "ADMIN", "COACH"]}><Shelled><CoachPage /></Shelled></ProtectedRoute>
          } />
          <Route path="/retention" element={
            <ProtectedRoute roles={["SUPER_ADMIN", "ADMIN", "COACH"]}><Shelled><RetentionPage /></Shelled></ProtectedRoute>
          } />
          <Route path="/referrals" element={
            <ProtectedRoute roles={["SUPER_ADMIN", "ADMIN"]}><Shelled><ReferralsPage /></Shelled></ProtectedRoute>
          } />
          <Route path="/reports" element={
            <ProtectedRoute roles={["SUPER_ADMIN", "ADMIN"]}><Shelled><ReportsPage /></Shelled></ProtectedRoute>
          } />
          <Route path="/finance" element={
            <ProtectedRoute roles={["SUPER_ADMIN", "ADMIN"]}><Shelled><FinancePage /></Shelled></ProtectedRoute>
          } />
          <Route path="/schedule" element={
            <ProtectedRoute roles={["SUPER_ADMIN", "ADMIN"]}><Shelled><SchedulePage /></Shelled></ProtectedRoute>
          } />
          <Route path="/me" element={
            <ProtectedRoute roles={["CUSTOMER"]}><Shelled><CustomerHubPage /></Shelled></ProtectedRoute>
          } />
          <Route path="/operations" element={
            <ProtectedRoute roles={["SUPER_ADMIN", "ADMIN", "COACH"]}><Shelled><OperationsPage /></Shelled></ProtectedRoute>
          } />
          <Route path="/approvals" element={
            <ProtectedRoute roles={["SUPER_ADMIN", "ADMIN"]}><Shelled><ApprovalsPage /></Shelled></ProtectedRoute>
          } />
          <Route path="/marketing" element={
            <ProtectedRoute roles={["SUPER_ADMIN", "ADMIN"]}><Shelled><MarketingPage /></Shelled></ProtectedRoute>
          } />
          <Route path="/rewards" element={
            <ProtectedRoute roles={["SUPER_ADMIN"]}><Shelled><RewardSettingsPage /></Shelled></ProtectedRoute>
          } />
          <Route path="/redemptions" element={
            <ProtectedRoute roles={["SUPER_ADMIN", "ADMIN"]}><Shelled><RedemptionsAdminPage /></Shelled></ProtectedRoute>
          } />
          <Route path="/jobs" element={
            <ProtectedRoute roles={["SUPER_ADMIN", "ADMIN", "COACH", "CUSTOMER"]}><Shelled><JobsPage /></Shelled></ProtectedRoute>
          } />
          <Route path="/assessments" element={
            <ProtectedRoute roles={["SUPER_ADMIN", "ADMIN", "COACH"]}><Shelled><AssessmentsPage /></Shelled></ProtectedRoute>
          } />
          <Route path="/staff" element={
            <ProtectedRoute roles={["SUPER_ADMIN"]}><Shelled><StaffPage /></Shelled></ProtectedRoute>
          } />
          <Route path="/expenses" element={
            <ProtectedRoute roles={["SUPER_ADMIN", "ADMIN"]}><Shelled><ExpensesPage /></Shelled></ProtectedRoute>
          } />
          <Route path="/website-embed" element={
            <ProtectedRoute roles={["SUPER_ADMIN", "ADMIN"]}><Shelled><WebsiteEmbedPage /></Shelled></ProtectedRoute>
          } />
          <Route path="/bulk-import" element={
            <ProtectedRoute roles={["SUPER_ADMIN"]}><Shelled><BulkImportPage /></Shelled></ProtectedRoute>
          } />

          <Route path="*" element={<RoleRedirect />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
